import './DashboardDetails.scss';

import { DataTableRowClickEventParams } from 'primereact/datatable';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import useAxiosHook from '../../../../hooks/useAxiosHook';
import useTableColumns from '../../../../hooks/useTableColumns';
import useTableState from '../../../../hooks/useTableState';
import { DashboardData } from '../../../../types/api/dashboard';
import { queryString } from '../../../../utils/helpers/http';
import { httpQueryObject } from '../../../../utils/helpers/misc';
import Table from '../../../DataTable/Table/Table';
import Flex from '../../../layout/flex/Flex';
import MainContent from '../../../layout/flex/MainContent';
import GoBackWithHistory from '../../../Misc/GoBack/GoBackWithHistory';
import Filters from '../../Components/Filters/Filters';
import HeaderPages from '../../Components/HeaderPages/HeaderPages';
import { SingleOrder } from '../../Orders/Orders.functions';
import {
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './DashboardDetails.functions';
import ViewDetailsDialog from './Dialogs/View/ViewDetailsDialog';
import useTableFilters from './useTableFilters';

function DashboardDetails() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<any>();
  const param = JSON.parse(id);
  if (!param.key) {
    history.push('/dashboard');
  }
  const [doubleClick, setIsDoubleClick] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    setSelection,
    setSortField,
    setSortOrder,
    setSelectionMultiple,
  } = useTableState<any>(tableStorageKey);

  const { headerFiltersCount, filters, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'dashboard_details',
      columnHeadersMap,
      columnHeadersMap,
      (c) => additionalColumnProperties(c as keyof typeof columnHeadersMap)
    );

  const { data, isLoading, reload, error } = useAxiosHook<DashboardData[]>({
    url:
      `/reports/orders/state/${param?.key}/details` +
      queryString(httpQueryObject(httpFiltersObj)),
  });

  function handleCMViewDoubleClick(e: DataTableRowClickEventParams) {
    if (!e.data) {
      return;
    }
    setSelection(e.data);
    setIsDoubleClick(true);
  }

  const { data: stateDetails, isLoading: isActiveDetailsDataLoading } =
    useAxiosHook<SingleOrder>(
      {
        url: `/orders/${selection?.id}`,
      },
      {
        skipWhen: !selection?.id || !showDetailsDialog,
      }
    );

  useEffect(() => {
    if (doubleClick && selection) {
      setShowDetailsDialog(true);
      setIsDoubleClick(false);
    }
  }, [selection, doubleClick, showDetailsDialog]);

  return (
    <div className="page dashboard-details">
      <GoBackWithHistory to="/dashboard" page={t('Dashboard')} />
      <HeaderPages title={param?.description} />

      <ViewDetailsDialog
        visible={showDetailsDialog}
        data={stateDetails}
        isLoading={isActiveDetailsDataLoading}
        onHide={() => setShowDetailsDialog(false)}
      />

      <Flex direction="column">
        <Filters
          filters={filters}
          resetAllFilters={resetAllFilters}
          headerFiltersCount={headerFiltersCount}
          filterHeight={160}
        />
        <MainContent>
          <Table
            tableRef={tableRef}
            filterHeight={160}
            columns={columns}
            data={data}
            isLoading={isLoading}
            reload={reload}
            isReloadDisabled={!id}
            hasError={!!error}
            headerTitle=""
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelectionMultiple}
            sortOrder={sortOrder}
            selection={selection}
            onRowDoubleClick={handleCMViewDoubleClick}
            storageString={tableStorageKey}
            selectionMode="single"
            clearSelectionObj={httpFiltersObj}
            selectionPageOnly
            rebuildTooltip
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
          />
        </MainContent>
      </Flex>
    </div>
  );
}

export default DashboardDetails;
